import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useApiCall } from "../services/api";
import { useDispatch, useSelector } from "react-redux";
import { Button, Pagination } from "antd";
import Loader from "../common/Loader";
import { formatDate, handleRouteNav } from "../utils";
import { perPageOptions, roleIds, scrollToTop } from "../constant";
import CustomBadge from "../components/Charts/CustomBadge";
import EmptySection from "../components/Layout/EmptySection";
import { notificationRefresh } from "../redux/auth/cartRefreshSlice";
import { setCounts } from "../redux/auth/navCountSlice";
import SearchInput from "../components/Forms/SearchInput";
import Filters from "../components/Filters";
import { PurgeButton, PurgeModalUI, usePurgeModal } from "../components/models/PurgeModal";
import ConfirmModal from "../components/models/ConfirmModal";
import usePagination from "../hooks/usePagination";
import PrimaryButton from "../components/Forms/PrimaryButton";

const Notification = () => {
  const { user_id, is_sales_manager, is_admin, role, role_id, customer_id, is_customer, is_sales_person, is_agent } = useSelector(
    (state) => state.auth,
  );
  const { appliedFilters, DrawerUI, FilterButton, resetFilters, hasFilters } = Filters({
    filters: [
      {
        type: "select",
        key: "category",
        label: "Category",
        options: [
          {
            label: "Order Details",
            value: "Order Details",
            roles: [roleIds.Customer, roleIds.SalesPerson, roleIds.SalesManager],
          },
          {
            label: "Booked Cases",
            value: "Booked Cases",
            roles: [roleIds.SalesPerson, roleIds.SalesManager],
          },
          {
            label: "Booked Folding",
            value: "Booked Folding",
            roles: [roleIds.SalesPerson, roleIds.SalesManager],
          },
          {
            label: "Booked Grey",
            value: "Booked Grey",
            roles: [roleIds.SalesPerson, roleIds.SalesManager],
          },
          {
            label: "My Profile",
            value: "My Profile",
            roles: [roleIds.Customer],
          },
          {
            label: "User Profile",
            value: "User Profile",
            roles: [roleIds.Admin, roleIds.SalesPerson, roleIds.SalesManager],
          },
          {
            label: "Customer Profile",
            value: "Customer Profile",
            roles: [roleIds.Admin, roleIds.SalesPerson, roleIds.SalesManager],
          },
          {
            label: "Modification Request Details",
            value: "Modification Request Details",
            roles: [roleIds.Admin, roleIds.SalesPerson, roleIds.SalesManager],
          },
          {
            label: "Discount Request Details",
            value: "Discount Request Details",
            roles: [roleIds.Admin, roleIds.SalesPerson, roleIds.SalesManager],
          },
          { label: "Announcement", value: "Announcement" },
        ].filter((option) => {
          const roles = option.roles || [];
          return roles.length === 0 || roles.includes(role_id);
        }),
      },
      {
        type: "daterange",
        key: "daterange",
        label: "Date",
      },
    ],
    onChange: () => resetPage(),
  });
  const { postApi, loading } = useApiCall();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [isReadAllConfirmOpen, setIsReadAllConfirmOpen] = useState(false);
  const dispatch = useDispatch();
  const { pagination, setPagination, resetPage } = usePagination();

  const { setTitle, setDescription, setBackButton } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const purgeHook = usePurgeModal();
  const fetchIdRef = useRef(0);

  const query = searchParams.get("q") || "";

  const onSearch = (value, _e, info) => console.log(info?.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    resetPage();
  };

  const payloadRoles = {
    1: "admin",
    2: "customer",
    3: "sales_person",
    4: "sales_manager",
  };

  const fetchNotifications = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    const { status, category, daterange } = appliedFilters;

    const filters = {
      status: status,
      notification_type: category,
      from_date: daterange?.[0] ? formatDate(new Date(daterange[0]), { format: "YYYY-MM-DD" }) : undefined,
      to_date: daterange?.[1] ? formatDate(new Date(daterange[1]), { format: "YYYY-MM-DD" }) : undefined,
    };

    try {
      const paginate = {
        page: pagination.current,
        per_page: pagination.pageSize,
        search: query,
        ...(Object.keys(appliedFilters).length ? filters : {}),
        ...(is_customer ? { user_id: customer_id } : is_sales_person ? { user_id: user_id } : {}),
        role: payloadRoles[role_id],
      };

      const data = await postApi(is_agent ? "sales/common/agent-notification" : "sales/common/notification", paginate);
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      setPagination({ total: data.total || 0 });
      setNotifications(data.data || []);
      dispatch(setCounts({ notificationCount: data.unread_count || 0 }));
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      console.error("Error fetching notifications:", err);
    }
  }, [appliedFilters, customer_id, is_customer, is_sales_person, pagination.current, pagination.pageSize, query, role_id, user_id]);

  const makeAsRead = async () => {
    const { status, category, daterange } = appliedFilters;

    const filters = {
      status: status,
      notification_type: category,
      from_date: daterange?.[0] ? formatDate(new Date(daterange[0]), { format: "YYYY-MM-DD" }) : undefined,
      to_date: daterange?.[1] ? formatDate(new Date(daterange[1]), { format: "YYYY-MM-DD" }) : undefined,
    };

    const res = await postApi(is_agent ? "sales/common/agent-notification" : "sales/common/notification", {
      search: query,
      role: payloadRoles[role_id],
      ...(Object.keys(appliedFilters).length ? filters : {}),
      ...(is_customer ? { user_id: customer_id } : is_sales_person ? { user_id: user_id } : {}),
    });

    const allNotifications = res.data || [];

    const unreadIds = (allNotifications || [])
      .filter((item) => Number(item?.unread_count) > 0)
      .flatMap((item) => (Array.isArray(item.notifications_ids) ? item.notifications_ids : item.notifications_ids ? [item.notifications_ids] : []))
      .filter(Boolean);

    if (!unreadIds.length) {
      setIsReadAllConfirmOpen(false);
      return;
    }

    try {
      if (is_agent == 1) {
        const ownNotifications = allNotifications.filter((item) => item.user_id === customer_id);
        const notOwnNotifications = allNotifications.filter((item) => item.user_id !== customer_id);

        const unreadOwnIds = ownNotifications
          .filter((item) => Number(item?.unread_count) > 0)
          .flatMap((item) =>
            Array.isArray(item.notifications_ids) ? item.notifications_ids : item.notifications_ids ? [item.notifications_ids] : [],
          )
          .filter(Boolean);

        const unreadNotOwnIds = notOwnNotifications
          .filter((item) => Number(item?.unread_count) > 0)
          .flatMap((item) =>
            Array.isArray(item.notifications_ids) ? item.notifications_ids : item.notifications_ids ? [item.notifications_ids] : [],
          )
          .filter(Boolean);

        const [ownRes, notOwnRes] = await Promise.all([
          unreadOwnIds.length > 0
            ? postApi("sales/common/view-notification", {
                id: unreadOwnIds,
                role: "Customer",
              })
            : Promise.resolve(),
          unreadNotOwnIds.length > 0
            ? postApi("sales/common/view-notification", {
                id: unreadNotOwnIds,
                role: "Agent",
              })
            : Promise.resolve(),
        ]);

        if ((ownRes && ownRes.status) || (notOwnRes && notOwnRes.status)) {
          fetchNotifications();
        }
      } else {
        const res = await postApi("sales/common/view-notification", {
          id: unreadIds,
          role: is_customer ? "Customer" : role,
        });

        if (res?.status) {
          fetchNotifications();
        }
      }
    } catch (err) {
      console.error("Error marking all notifications as seen:", err);
    } finally {
      setIsReadAllConfirmOpen(false);
    }
  };

  const handlePurge = async (payload) => {
    const response = await postApi(
      "sales/common/delete-notification",
      {
        ...payload,
        user_id: is_customer ? customer_id : undefined,
        user_type: is_customer ? "Customer" : "User",
      },
      true,
    );

    if (response && response.status) {
      dispatch(notificationRefresh());
      fetchNotifications();
    }

    return response.status || false;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchNotifications();
    }, 300);

    return () => clearTimeout(handler);
  }, [fetchNotifications]);

  useEffect(() => {
    setTitle("Notifications");
    setDescription(`${pagination.total} total notifications`);
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton, pagination.total]);

  const seenField = is_sales_manager ? "is_salesmanger" : is_admin ? "is_admin" : is_customer ? "is_customer" : "is_salesperson";

  const handleNotifView = async (notification) => {
    // const isSeen =
    //   (is_sales_manager
    //     ? notification.is_salesmanger
    //     : is_admin
    //       ? notification.is_admin
    //       : is_customer
    //         ? notification.is_customer
    //         : notification.is_salesperson) || false;

    // if (isSeen || notification.id === undefined) return;

    const numberOfUnread = Number(notification.unread_count) || 0;
    if (numberOfUnread <= 0) {
      return;
    }

    try {
      const res = await postApi("sales/common/view-notification", {
        id: notification.notifications_ids,
        role: is_customer ? "Customer" : role,
      });

      if (res && res.status) {
        // Update local state to mark this notification as seen
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === notification.id
              ? {
                  ...notif,
                  [seenField]: 1,
                }
              : notif,
          ),
        );
      }
    } catch (err) {
      console.error("Error marking notification as seen:", err);
    }
  };

  const getMainData = {
    "Order Details": {
      navCallBack: ({ notificationTypeId }) => {
        navigate(handleRouteNav(`/orders/${notificationTypeId}`, role_id), {
          state: { fromNotification: true },
        });
      },
      btnText: "Order Details",
      skipView: true,
    },
    "My Profile": {
      navCallBack: ({ notificationTypeId }) => {
        navigate(handleRouteNav(`/profile`, role_id), {
          state: {
            user_id: notificationTypeId,
            routeType: is_customer ? "client" : "user",
          },
        });
      },
      btnText: "View Profile",
    },
    "User Profile": {
      navCallBack: ({ notificationTypeId }) => {
        navigate(handleRouteNav(notificationTypeId === user_id ? "/profile" : is_admin ? `/user-profile` : `/sales-profile`, role_id), {
          state: {
            user_id: notificationTypeId,
            routeType: notificationTypeId === user_id ? "user" : "sales",
          },
        });
      },
      btnText: "View Profile",
    },
    "Modification Request Details": {
      navCallBack: ({ notificationTypeId, requestCustomerId }) => {
        navigate(handleRouteNav(`/modification-profile`, role_id), {
          state: {
            user_id: requestCustomerId,
            customer_request_id: notificationTypeId,
            routeType: "client",
            from_modification: true,
          },
        });
      },
      btnText: "View Details",
    },
    "Modificaton Request Details": {
      navCallBack: ({ notificationTypeId, requestCustomerId }) => {
        navigate(handleRouteNav(`/modification-profile`, role_id), {
          state: {
            user_id: requestCustomerId,
            customer_request_id: notificationTypeId,
            routeType: "client",
            from_modification: true,
          },
        });
      },
      btnText: "View Details",
    },
    "Discount Request Details": {
      navCallBack: ({ notificationTypeId, requestCustomerId }) => {
        navigate(handleRouteNav(`/scheme-details`, role_id), {
          state: {
            offerRequestId: notificationTypeId,
            offerId: requestCustomerId,
          },
        });
      },
      btnText: "View Details",
    },
    "Booked Cases": {
      navCallBack: ({ notificationTypeId }) => {
        navigate(handleRouteNav(`/product-catalog/all-stocks`, role_id), {
          state: { fromNotification: true, notificationTypeId },
        });
      },
      btnText: "View Stocks",
      skipView: true,
    },
    "Booked Folding": {
      navCallBack: ({ notificationTypeId }) => {
        navigate(handleRouteNav(`/product-catalog/folding`, role_id), {
          state: { fromNotification: true, notificationTypeId },
        });
      },
      btnText: "View Stocks",
      skipView: true,
    },
    "Booked Grey": {
      navCallBack: ({ notificationTypeId }) => {
        navigate(handleRouteNav(`/product-catalog/grey`, role_id), {
          state: { fromNotification: true, notificationTypeId },
        });
      },
      btnText: "View Stocks",
      skipView: true,
    },
    "Customer Profile": {
      navCallBack: ({ notificationTypeId }) => {
        navigate(handleRouteNav(notificationTypeId === customer_id && is_customer ? "/profile" : `/client-profile`, role_id), {
          state: {
            user_id: notificationTypeId,
            routeType: notificationTypeId === customer_id && is_customer ? "user" : "client",
          },
        });
      },
      btnText: "View Profile",
    },
    Announcement: {
      btnText: "View",
      navCallBack: ({ notification }) => {
        if (Number(notification.unread_count) > 0) {
          fetchNotifications();
          dispatch(notificationRefresh());
        }
      },
    },
  };

  return (
    <>
      <div className="flex flex-col pb-10 gap-3">
        <div className="bg-[#FFFFFF] mb-6" style={{ borderRadius: "32px", overflow: "hidden" }}>
          <div className="flex justify-between gap-7 items-center px-8 py-6">
            <SearchInput onSearch={onSearch} value={query} onChange={handleOnSearchChange} placeholder="Search for notifications" />
            <div className="flex gap-3 items-center">
              <FilterButton />
              {hasFilters && is_customer && (
                <PrimaryButton btnType="secondary" text="Remove" onClick={() => resetFilters()} textClassName="" className="!w-[130px]" />
              )}
              {notifications.length > 0 && (
                <Button type="text" danger onClick={() => setIsReadAllConfirmOpen(true)} className="text-sm">
                  {"Read All"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : notifications.length === 0 ? (
          is_customer ? (
            <>
              <div className="flex justify-center">
                <div>
                  <img src="/assets/icons/EmptySearch.svg"></img>
                  <p className="text-[#000] mb-8">Sorry! We couldn't find any matches.</p>
                </div>
              </div>
            </>
          ) : (
            <EmptySection />
          )
        ) : (
          notifications.map((item) => {
            const { btnText, navCallBack, skipView = false } = getMainData[item.notification_type] || {};

            return (
              <div key={item.id} className="bg-white p-6 rounded-3xl border border-[#D9D9D9] flex flex-col">
                {/* Text Content */}
                <div className="flex flex-col gap-2 text-left">
                  <div className="flex w-full justify-between">
                    <p className="text-[#000000] text-lg font-semibold">{item.title}</p>
                    {Number(item.unread_count) > 0 && <CustomBadge text={`New (${item.unread_count})`} variant="primary" />}
                  </div>
                  <p className="text-[#000000] text-base leading-6 whitespace-pre-line">{item.message?.replace(/\\n/g, "\n")}</p>

                  {/* ✅ Date + Button on same line */}
                  <div className="flex justify-between items-center text-sm">
                    <p className="text-[#666666]">
                      {formatDate(item.created_at, {
                        format: "D MMM YYYY h:mma",
                      })}
                      {item.updated_by && ` • Updated by: ${item.updated_by}`}
                    </p>
                    {btnText && (
                      <button
                        onClick={async () => {
                          if (navCallBack) {
                            !skipView && (await handleNotifView(item));
                            scrollToTop();
                            navCallBack({
                              notificationTypeId: item.notification_type_id,
                              requestCustomerId: item.request_customer_id,
                              notification: item,
                            });
                          }
                        }}
                        className="text-[#A33434] font-medium hover:font-semibold transition-all duration-300"
                        disabled={!navCallBack}
                      >
                        {btnText} &gt;
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}

        {pagination.total > 0 && !loading && (
          <>
            <div className="flex justify-end mt-6 pr-6">
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={(page, pageSize) => {
                  setPagination((prev) => ({
                    current: pageSize !== prev.pageSize ? 1 : page,
                    pageSize,
                  }));
                }}
                showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
                showSizeChanger={true}
                pageSizeOptions={perPageOptions}
              />
            </div>

            {!is_sales_person && (
              <div className="flex justify-end mt-4">
                <PurgeButton hookData={purgeHook} />
              </div>
            )}
          </>
        )}
      </div>
      <DrawerUI />
      <PurgeModalUI hookData={purgeHook} onConfirm={handlePurge} loading={loading} />
      <ConfirmModal
        actionKey={"GENERIC_CONFIRM"}
        open={isReadAllConfirmOpen}
        onCancel={() => setIsReadAllConfirmOpen(false)}
        onConfirm={makeAsRead}
        title="Read All"
        description="Do you want to mark all as read?"
        confirmText="Read All"
        loading={loading}
      />
    </>
  );
};

export default Notification;
